/**
 * ICF official form statement catalog (rev. 2026) — frontend mirror.
 *
 * GENERATED from `backend/app/data/frameworks/icf_forms/forms_2026.json`
 * (the machine-readable catalog described in the Mentor Engagement SOF/CRF spec).
 * Do not hand-edit: regenerate from that file when the official forms change.
 *
 * This is the single source of statement text for the mentor workspace UI and
 * for the dev-only mock layer in `services/api/mentorEngagementsAPI.ts`.
 */

export type IcfFormLevel = 'acc' | 'pcc' | 'mcc';
export type IcfCompetencyCode = 'C3' | 'C4' | 'C5' | 'C6' | 'C7' | 'C8';

/** ACC/MCC use a 4-point BARS + N/A; PCC uses a binary Observed / Not Observed. */
export type BarsRatingValue = 'exceeds' | 'meets' | 'below' | 'does_not_meet' | 'not_applicable';
export type BinaryRatingValue = 'observed' | 'not_observed';
export type StatementRatingValue = BarsRatingValue | BinaryRatingValue;

export interface IcfFormStatement {
  /** Canonical official reference — `A3.1` (ACC), `3.1` (PCC), `M3.1` (MCC). */
  id: string;
  competency_code: IcfCompetencyCode;
  text_en: string;
  text_tr: string;
  /** Rendered beneath the statement (only PCC 6.4 has one today). */
  note_en?: string;
  note_tr?: string;
}

export interface IcfScaleOption {
  value: StatementRatingValue;
  label_en: string;
  label_tr: string;
  definition_en?: string;
  definition_tr?: string;
}

export interface IcfFormScale {
  kind: 'bars' | 'binary';
  options: IcfScaleOption[];
}

export interface IcfCompetencyBlock {
  code: IcfCompetencyCode;
  title_en: string;
  title_tr: string;
  /** CRF gate: at least `required` of `total` statements must be demonstrated. */
  required: number;
  total: number;
  statements: IcfFormStatement[];
}

export interface IcfLevelForm {
  level: IcfFormLevel;
  /** Revision stamp printed on the generated Session Observation Form. */
  sof_revision: string;
  /** Revision stamp printed on the generated Competency Review Form. */
  crf_revision: string;
  scale: IcfFormScale;
  competencies: IcfCompetencyBlock[];
}

export interface IcfC1Qualifier {
  key: 'q1' | 'q2';
  text_en: string;
  text_tr: string;
}

/** C1 qualifier scale is the binary Observed / Not Observed at every level. */
export const C1_QUALIFIER_OPTIONS: IcfScaleOption[] = [
  { value: 'observed', label_en: 'Observed', label_tr: 'Gözlemlendi' },
  { value: 'not_observed', label_en: 'Not Observed', label_tr: 'Gözlemlenmedi' },
];

export const C1_QUALIFIERS: IcfC1Qualifier[] = [
  { key: 'q1', text_en: 'Coach demonstrates alignment with the ICF Code of Ethics.', text_tr: 'Koç, ICF Etik Kurallarıyla uyumu gösterir.' },
  { key: 'q2', text_en: 'Coach is in consistent alignment with the role of a coach.', text_tr: 'Koç, koç rolüyle tutarlı bir uyum içindedir.' },
];

/** C1 verification statement on the CRF (single Yes/No + notes). */
export const C1_CRF_VERIFICATION = {
  text_en: 'I verify that the client has demonstrated understanding and consistent application of coaching ethics during the engagement.    Yes        No',
  text_tr: 'Müşterinin, koçluk bağlantısı boyunca koçluk etik kurallarını anlama ve tutarlılıkla uygulama konusunda yetkinlik gösterdiğini doğrularım. ☐ Evet   ☐ Hayır',
  notes_label_en: 'Notes (e.g., confidentiality, boundaries, conflicts of interest):',
  notes_label_tr: 'Notlar (gizlilik, sınırlar, çıkar çatışmaları):',
} as const;

/**
 * C2 has no behavioural statements at ACC/MCC. At PCC it is a *view* over these
 * 11 markers — never scored separately, never double-counted.
 */
export const PCC_C2_PROXY_MARKER_IDS: string[] = [
  '4.1', '4.3', '4.4', '5.1', '5.2', '5.3', '5.4', '6.1', '6.5', '7.1', '7.5',
];

export const ICF_FORMS: Record<IcfFormLevel, IcfLevelForm> = {
  acc: {
    level: 'acc',
    sof_revision: '06.18.26',
    crf_revision: '06.18.26',
    scale: {
    kind: 'bars',
    options: [
      { value: 'exceeds', label_en: 'Exceeds Standard', label_tr: 'Standardı Aşıyor', definition_en: 'Exceeds the standard (exemplary demonstration).', definition_tr: 'Standardı Aşıyor – Örnek düzeyde gösterim' },
      { value: 'meets', label_en: 'Meets Standard', label_tr: 'Standardı Karşılıyor', definition_en: 'Meets the standard (proficient demonstration).', definition_tr: 'Standardı Karşılıyor – Yetkin düzeyde gösterim' },
      { value: 'below', label_en: 'Below Standard', label_tr: 'Standardın Altında', definition_en: 'Below the standard (partial demonstration).', definition_tr: 'Standardın Altında – Kısmi gösterim' },
      { value: 'does_not_meet', label_en: 'Does Not Meet Standard', label_tr: 'Standardı Karşılamıyor', definition_en: 'Does not meet standard (not demonstrated).', definition_tr: 'Standardı Karşılamıyor – Gösterilmedi' },
      { value: 'not_applicable', label_en: 'N/A', label_tr: 'Geçerli Değil (GD)', definition_en: 'N/A – Not applicable (to be used in rare cases in which the behavior as described by the statement was not relevant to the conversation or there were no opportunities for the coach to demonstrate the behavior).', definition_tr: 'Geçerli Değil (GD) – Davranış, görüşme bağlamında geçerli değildi veya koçun göstermesi için fırsat yoktu' },
    ],
    },
    competencies: [
      {
        code: 'C3',
        title_en: 'Establishes and Maintains Agreements',
        title_tr: 'Anlaşmaları Oluşturur ve Sürdürür',
        required: 3,
        total: 4,
        statements: [
          {
            id: 'A3.1',
            competency_code: 'C3',
            text_en: 'Coach explores the client\'s topic with the client',
            text_tr: 'Koç, müşterinin gündem konusunu müşteriyle birlikte araştırır / keşfeder.',
          },
          {
            id: 'A3.2',
            competency_code: 'C3',
            text_en: 'Coach and client reach agreement on what the client wants to accomplish as a session outcome.',
            text_tr: 'Koç ve müşteri, müşterinin görüşme çıktısı olarak gerçekleştirmek istediği konusunda anlaşmaya varır.',
          },
          {
            id: 'A3.3',
            competency_code: 'C3',
            text_en: 'Coach explores the significance of the coaching outcome to the client.',
            text_tr: 'Koç, koçluk çıktısının müşteri için önemini araştırır / keşfeder.',
          },
          {
            id: 'A3.4',
            competency_code: 'C3',
            text_en: 'Coach attends to the agreed upon agenda throughout the session.',
            text_tr: 'Koç, görüşme boyunca üzerinde anlaşılan gündeme bağlı kalır.',
          },
        ],
      },
      {
        code: 'C4',
        title_en: 'Cultivates Trust and Safety',
        title_tr: 'İtimat ve Güven Oluşturur',
        required: 2,
        total: 3,
        statements: [
          {
            id: 'A4.1',
            competency_code: 'C4',
            text_en: 'Coach acknowledges the client\'s work in the session.',
            text_tr: 'Koç, müşterinin görüşmedeki çalışmasını kabul eder / takdir eder.',
          },
          {
            id: 'A4.2',
            competency_code: 'C4',
            text_en: 'Coach expresses respect, support, or concern for the client.',
            text_tr: 'Koç, müşteriye saygı, destek veya ilgi ifade eder.',
          },
          {
            id: 'A4.3',
            competency_code: 'C4',
            text_en: 'Coach supports the client\'s expression of feelings, perceptions, concerns, or beliefs.',
            text_tr: 'Koç, müşterinin duygu, algı, endişe veya inançlarını ifade etmesini kabul eder ve destekler.',
          },
        ],
      },
      {
        code: 'C5',
        title_en: 'Maintains Presence',
        title_tr: 'Koç Var Olma Halini Sürdürür',
        required: 3,
        total: 4,
        statements: [
          {
            id: 'A5.1',
            competency_code: 'C5',
            text_en: 'Coach is observant and responsive to the client.',
            text_tr: 'Koç, gözlemci ve müşterinin ihtiyaçlarına cevap veren bir tutum sergiler.',
          },
          {
            id: 'A5.2',
            competency_code: 'C5',
            text_en: 'Coach demonstrates curiosity about the client, or their agenda, or both.',
            text_tr: 'Koç, müşteri hakkında ya da müşterinin gündemi hakkında ya da her ikisi hakkında merak gösterir.',
          },
          {
            id: 'A5.3',
            competency_code: 'C5',
            text_en: 'Coach provides space for the client to lead during the session.',
            text_tr: 'Koç, görüşme boyunca müşterinin yönlendirici olmasına alan açar.',
          },
          {
            id: 'A5.4',
            competency_code: 'C5',
            text_en: 'Coach is silent to allow time for the client to reflect.',
            text_tr: 'Koç, müşteriye yansıma zamanı tanımak için sessizliği kullanır.',
          },
        ],
      },
      {
        code: 'C6',
        title_en: 'Listens Actively',
        title_tr: 'Etkin Dinler',
        required: 2,
        total: 3,
        statements: [
          {
            id: 'A6.1',
            competency_code: 'C6',
            text_en: 'Coach listens by recognizing feelings, perceptions, challenges, or beliefs.',
            text_tr: 'Koç, duyguları, algıları, zorlukları veya inançları fark ederek dinler.',
          },
          {
            id: 'A6.2',
            competency_code: 'C6',
            text_en: 'Coach inquires about, explores, or includes the client\'s use of language.',
            text_tr: 'Koç, müşterinin kullandığı dili sorar, araştırır veya buna dahil olur.',
          },
          {
            id: 'A6.3',
            competency_code: 'C6',
            text_en: 'Coach summarizes or paraphrases what the client communicates to confirm the coach\'s understanding.',
            text_tr: 'Koç, kendi anlayışını doğrulamak amacıyla müşterinin ilettiğini özetler veya parafraz yapar.',
          },
        ],
      },
      {
        code: 'C7',
        title_en: 'Evokes Awareness',
        title_tr: 'Farkındalık Yaratır',
        required: 2,
        total: 3,
        statements: [
          {
            id: 'A7.1',
            competency_code: 'C7',
            text_en: 'Coach supports the client in viewing the situation from different perspectives.',
            text_tr: 'Koç, müşterinin durumu farklı perspektiflerden görmesini destekler.',
          },
          {
            id: 'A7.2',
            competency_code: 'C7',
            text_en: 'Coach inquires about the client\'s feelings, perceptions, behaviors, or beliefs.',
            text_tr: 'Koç, müşterinin duyguları, algıları, davranışları veya inançları hakkında sorular sorar.',
          },
          {
            id: 'A7.3',
            competency_code: 'C7',
            text_en: 'Coach asks clear, open-ended questions, one at a time.',
            text_tr: 'Koç, bir seferde bir tane olmak üzere, açık ve açık uçlu sorular sorar.',
          },
        ],
      },
      {
        code: 'C8',
        title_en: 'Facilitates Client Growth',
        title_tr: 'Müşterinin Gelişimini Kolaylaştırır',
        required: 2,
        total: 3,
        statements: [
          {
            id: 'A8.1',
            competency_code: 'C8',
            text_en: 'Coach asks questions about what the client has learned during the session.',
            text_tr: 'Koç, müşterinin görüşme sırasında öğrendikleri hakkında sorular sorar.',
          },
          {
            id: 'A8.2',
            competency_code: 'C8',
            text_en: 'Coach supports the client to use their learning to plan next steps.',
            text_tr: 'Koç, müşteriyi öğrendiklerini sonraki adımları planlamak için kullanmaya destekler.',
          },
          {
            id: 'A8.3',
            competency_code: 'C8',
            text_en: 'Coach supports the client to close the session.',
            text_tr: 'Koç, müşteriyi görüşmeyi kapatmaya destekler; görüşmeyi kapatmak için müşteriyle iş birliği yapar.',
          },
        ],
      },
    ],
  },
  pcc: {
    level: 'pcc',
    sof_revision: '07.02.26',
    crf_revision: '06.18.26',
    scale: {
    kind: 'binary',
    options: [
      { value: 'observed', label_en: 'Observed', label_tr: 'Gözlemlendi', definition_en: 'Select Observed or Not Observed based on the evidence found in the session.', definition_tr: 'Görüşmede bulunan kanıta dayanarak \'Gözlemlendi\' veya \'Gözlemlenmedi\' seçeneğini işaretleyiniz.' },
      { value: 'not_observed', label_en: 'Not Observed', label_tr: 'Gözlemlenmedi' },
    ],
    },
    competencies: [
      {
        code: 'C3',
        title_en: 'Establishes and Maintains Agreements',
        title_tr: 'Anlaşmaları Oluşturur ve Sürdürür',
        required: 3,
        total: 4,
        statements: [
          {
            id: '3.1',
            competency_code: 'C3',
            text_en: 'Coach partners with the client to identify or reconfirm what the client wants to accomplish in this session.',
            text_tr: 'Koç, müşterinin bu görüşmede gerçekleştirmek istediği şeyi belirlemek veya teyit etmek için müşteriyle iş birliği yapar.',
          },
          {
            id: '3.2',
            competency_code: 'C3',
            text_en: 'Coach partners with the client to define or reconfirm measure(s) of success for what the client wants to accomplish in this session.',
            text_tr: 'Koç, müşterinin bu görüşmede gerçekleştirmek istediği şey için başarı ölçütünü/ölçütlerini tanımlamak veya teyit etmek üzere müşteriyle iş birliği yapar.',
          },
          {
            id: '3.3',
            competency_code: 'C3',
            text_en: 'Coach inquires about or explores what is important or meaningful to the client about what they want to accomplish in this session.',
            text_tr: 'Koç, müşterinin bu görüşmede gerçekleştirmek istediği şey konusunda müşteri için önemli veya anlamlı olanı sorar ya da araştırır.',
          },
          {
            id: '3.4',
            competency_code: 'C3',
            text_en: 'Coach partners with the client to define what the client believes they need to address to achieve what they want to accomplish in this session.',
            text_tr: 'Koç, müşterinin bu görüşmede gerçekleştirmek istediğine ulaşmak için ele alınması gerektiğine inandığı şeyi tanımlamak üzere müşteriyle iş birliği yapar.',
          },
        ],
      },
      {
        code: 'C4',
        title_en: 'Cultivates Trust and Safety',
        title_tr: 'İtimat ve Güven Oluşturur',
        required: 3,
        total: 4,
        statements: [
          {
            id: '4.1',
            competency_code: 'C4',
            text_en: 'Coach acknowledges and respects the client\'s unique talents, insights and work in the coaching process.',
            text_tr: 'Koç, koçluk süreci boyunca müşterinin kendine has yeteneklerini, içgörülerini ve çalışmasını kabul eder ve saygı gösterir.',
          },
          {
            id: '4.2',
            competency_code: 'C4',
            text_en: 'Coach shows support, empathy or concern for the client.',
            text_tr: 'Koç, müşteriye destek, empati veya ilgi gösterir.',
          },
          {
            id: '4.3',
            competency_code: 'C4',
            text_en: 'Coach acknowledges and supports the client\'s expression of feelings, perceptions, concerns, beliefs or suggestions.',
            text_tr: 'Koç, müşterinin duygu, algı, endişe, inanç veya önerilerini ifade etmesini kabul eder ve destekler.',
          },
          {
            id: '4.4',
            competency_code: 'C4',
            text_en: 'Coach partners with the client by inviting the client to respond in any way to the coach\'s contributions and accepts the client\'s response.',
            text_tr: 'Koç, müşteriyi kendi katkılarına herhangi bir şekilde yanıt vermeye davet ederek ve müşterinin yanıtını kabul ederek iş birliği yapar.',
          },
        ],
      },
      {
        code: 'C5',
        title_en: 'Maintains Presence',
        title_tr: 'Koç Var Olma Halini Sürdürür',
        required: 4,
        total: 5,
        statements: [
          {
            id: '5.1',
            competency_code: 'C5',
            text_en: 'Coach acts in response to the whole person of the client (the who).',
            text_tr: 'Koç, müşterinin bütününe (kim olduğuna) yanıt verecek şekilde hareket eder.',
          },
          {
            id: '5.2',
            competency_code: 'C5',
            text_en: 'Coach acts in response to what the client wants to accomplish throughout this session (the what).',
            text_tr: 'Koç, müşterinin bu görüşme boyunca gerçekleştirmek istediğine (neye) yanıt verecek şekilde hareket eder.',
          },
          {
            id: '5.3',
            competency_code: 'C5',
            text_en: 'Coach partners with the client by supporting the client to choose what happens in this session.',
            text_tr: 'Koç, müşterinin bu görüşmede neler olacağını seçmesini destekleyerek müşteriyle iş birliği yapar.',
          },
          {
            id: '5.4',
            competency_code: 'C5',
            text_en: 'Coach demonstrates curiosity to learn more about the client.',
            text_tr: 'Koç, müşteri hakkında daha fazla bilgi edinmeye yönelik merak gösterir.',
          },
          {
            id: '5.5',
            competency_code: 'C5',
            text_en: 'Coach allows for silence, pause or reflection.',
            text_tr: 'Koç, sessizliğe, duraklamaya veya yansımaya alan tanır.',
          },
        ],
      },
      {
        code: 'C6',
        title_en: 'Listens Actively',
        title_tr: 'Etkin Dinler',
        required: 5,
        total: 7,
        statements: [
          {
            id: '6.1',
            competency_code: 'C6',
            text_en: 'Coach\'s questions and observations are customized by using what the coach has learned about who the client is or the client\'s situation.',
            text_tr: 'Koç, müşterinin kim olduğu veya durumu hakkında öğrendiklerini kullanarak sorularını ve gözlemlerini kişiselleştirir.',
          },
          {
            id: '6.2',
            competency_code: 'C6',
            text_en: 'Coach inquires about or explores the words the client uses.',
            text_tr: 'Koç, müşterinin kullandığı sözcükleri sorar veya araştırır.',
          },
          {
            id: '6.3',
            competency_code: 'C6',
            text_en: 'Coach inquires about or explores the client\'s emotions.',
            text_tr: 'Koç, müşterinin duygularını sorar veya araştırır.',
          },
          {
            id: '6.4',
            competency_code: 'C6',
            text_en: 'Coach explores the client\'s energy shifts, nonverbal cues or other behaviors.',
            text_tr: 'Koç, müşterinin enerji değişimlerini, sözsüz ipuçlarını veya diğer davranışlarını araştırır.',
            note_en: 'If there is no distinct shift in client\'s energy, nonverbal cues or other behavior, mark the marker as \'Observed\'.',
            note_tr: 'Müşterinin enerjisinde, sözsüz ipuçlarında veya diğer davranışlarında belirgin bir değişim yoksa, işaretçiyi \'Gözlemlendi\' olarak işaretleyiniz.',
          },
          {
            id: '6.5',
            competency_code: 'C6',
            text_en: 'Coach inquiries about or explores how the client currently perceives themself or their world.',
            text_tr: 'Koç, müşterinin kendisini veya dünyasını şu anda nasıl algıladığını sorar veya araştırır.',
          },
          {
            id: '6.6',
            competency_code: 'C6',
            text_en: 'Coach allows the client to complete speaking without interrupting unless there is a stated coaching purpose to do so.',
            text_tr: 'Koç, belirtilmiş bir koçluk amacı olmadıkça müşterinin sözünü kesmeden konuşmasını tamamlamasına izin verir.',
          },
          {
            id: '6.7',
            competency_code: 'C6',
            text_en: 'Coach succinctly reflects or summarizes what the client communicated to ensure the client\'s clarity and understanding.',
            text_tr: 'Koç, müşterinin ilettiklerini, netlik ve anlayışını sağlamak amacıyla özlü bir şekilde yansıtır veya özetler.',
          },
        ],
      },
      {
        code: 'C7',
        title_en: 'Evokes Awareness',
        title_tr: 'Farkındalık Yaratır',
        required: 6,
        total: 8,
        statements: [
          {
            id: '7.1',
            competency_code: 'C7',
            text_en: 'Coach asks questions about the client, such as their current way of thinking, feeling, values, needs, wants, beliefs or behavior.',
            text_tr: 'Koç, müşterinin mevcut düşünme biçimi, hissi, değerleri, ihtiyaçları, istekleri veya inançları/davranışları hakkında sorular sorar.',
          },
          {
            id: '7.2',
            competency_code: 'C7',
            text_en: 'Coach asks questions to help the client explore beyond the client\'s current thinking or feeling to new or expanded ways of thinking or feeling about themself (the who).',
            text_tr: 'Koç, müşterinin kendisi (kim) hakkında mevcut düşünce veya hissinin ötesine geçerek yeni veya genişlemiş düşünme/hissetme biçimlerini keşfetmesine yardımcı olacak sorular sorar.',
          },
          {
            id: '7.3',
            competency_code: 'C7',
            text_en: 'Coach asks questions to help the client explore beyond the client\'s current thinking or feeling to new or expanded ways of thinking or feeling about their situation (the what).',
            text_tr: 'Koç, müşterinin durumu (ne) hakkında mevcut düşünce veya hissinin ötesine geçerek yeni veya genişlemiş düşünme/hissetme biçimlerini keşfetmesine yardımcı olacak sorular sorar.',
          },
          {
            id: '7.4',
            competency_code: 'C7',
            text_en: 'Coach asks questions to help the client explore beyond current thinking, feeling or behaving toward the outcome the client desires.',
            text_tr: 'Koç, müşterinin mevcut düşünme, hissetme veya davranışının ötesine geçerek arzuladığı sonuca doğru keşfetmesine yardımcı olacak sorular sorar.',
          },
          {
            id: '7.5',
            competency_code: 'C7',
            text_en: 'Coach shares – with no attachment – observations, intuitions, comments, thoughts or feelings, and invites the client\'s exploration through verbal or tonal invitation.',
            text_tr: 'Koç, hiçbir bağlanma göstermeden gözlemlerini, sezgilerini, yorumlarını, düşüncelerini veya duygularını paylaşır ve müşteriyi sözlü ya da tonlama yoluyla araştırmaya davet eder.',
          },
          {
            id: '7.6',
            competency_code: 'C7',
            text_en: 'Coach asks clear, direct, primarily open-ended questions, one at a time, at a pace that allows for thinking, feeling or reflection by the client.',
            text_tr: 'Koç, müşteriye düşünme, hissetme veya yansıma zamanı tanıyan bir hızda, bir seferde bir tane olmak üzere açık, doğrudan ve ağırlıklı olarak açık uçlu sorular sorar.',
          },
          {
            id: '7.7',
            competency_code: 'C7',
            text_en: 'Coach uses language that is generally clear and concise.',
            text_tr: 'Koç, genel olarak açık ve özlü bir dil kullanır.',
          },
          {
            id: '7.8',
            competency_code: 'C7',
            text_en: 'Coach allows the client to do most of the talking.',
            text_tr: 'Koç, müşterinin büyük bölümünü konuşmasına izin verir.',
          },
        ],
      },
      {
        code: 'C8',
        title_en: 'Facilitates Client Growth',
        title_tr: 'Müşterinin Gelişimini Kolaylaştırır',
        required: 6,
        total: 9,
        statements: [
          {
            id: '8.1',
            competency_code: 'C8',
            text_en: 'Coach invites or allows the client to explore progress toward what the client wanted to accomplish in this session.',
            text_tr: 'Koç, müşteriyi bu görüşmede gerçekleştirmek istediğine doğru ilerlemeyi araştırmaya davet eder veya buna izin verir.',
          },
          {
            id: '8.2',
            competency_code: 'C8',
            text_en: 'Coach invites the client to state or explore the client\'s learning in this session about themself (the who).',
            text_tr: 'Koç, müşteriyi bu görüşmede kendisi (kim) hakkında öğrendiklerini ifade etmeye veya araştırmaya davet eder.',
          },
          {
            id: '8.3',
            competency_code: 'C8',
            text_en: 'Coach invites the client to state or explore the client\'s learning in this session about their situation (the what).',
            text_tr: 'Koç, müşteriyi bu görüşmede durumu (ne) hakkında öğrendiklerini ifade etmeye veya araştırmaya davet eder.',
          },
          {
            id: '8.4',
            competency_code: 'C8',
            text_en: 'Coach invites the client to consider how they will use new learning from this coaching session.',
            text_tr: 'Koç, müşteriyi bu koçluk görüşmesinden edindiği yeni öğrenimi nasıl kullanacağını düşünmeye davet eder.',
          },
          {
            id: '8.5',
            competency_code: 'C8',
            text_en: 'Coach partners with the client to design post-session thinking, reflection or action.',
            text_tr: 'Koç, görüşme sonrası düşünme, yansıma veya eylemi tasarlamak için müşteriyle iş birliği yapar.',
          },
          {
            id: '8.6',
            competency_code: 'C8',
            text_en: 'Coach partners with the client to consider how to move forward, including resources, support or potential barriers.',
            text_tr: 'Koç, kaynaklar, destek veya potansiyel engeller dahil olmak üzere nasıl ilerleyeceğini düşünmek için müşteriyle iş birliği yapar.',
          },
          {
            id: '8.7',
            competency_code: 'C8',
            text_en: 'Coach partners with the client to design the best methods of accountability for themself.',
            text_tr: 'Koç, müşterinin kendisi için en iyi hesap verebilirlik yöntemlerini tasarlamak üzere müşteriyle iş birliği yapar.',
          },
          {
            id: '8.8',
            competency_code: 'C8',
            text_en: 'Coach celebrates the client\'s progress and learning.',
            text_tr: 'Koç, müşterinin ilerlemesini ve öğrenimini kutlar.',
          },
          {
            id: '8.9',
            competency_code: 'C8',
            text_en: 'Coach partners with the client on how they want to complete this session.',
            text_tr: 'Koç, müşterinin bu görüşmeyi nasıl tamamlamak istediği konusunda müşteriyle iş birliği yapar.',
          },
        ],
      },
    ],
  },
  mcc: {
    level: 'mcc',
    sof_revision: '06.18.26',
    crf_revision: '06.18.26',
    scale: {
    kind: 'bars',
    options: [
      { value: 'exceeds', label_en: 'Exceeds Standard', label_tr: 'Standardı Aşıyor', definition_en: 'Exceeds the standard (exemplary demonstration).', definition_tr: 'Standardı Aşıyor – Örnek düzeyde gösterim' },
      { value: 'meets', label_en: 'Meets Standard', label_tr: 'Standardı Karşılıyor', definition_en: 'Meets the standard (proficient demonstration).', definition_tr: 'Standardı Karşılıyor – Yetkin düzeyde gösterim' },
      { value: 'below', label_en: 'Below Standard', label_tr: 'Standardın Altında', definition_en: 'Below the standard (partial demonstration).', definition_tr: 'Standardın Altında – Kısmi gösterim' },
      { value: 'does_not_meet', label_en: 'Does Not Meet Standard', label_tr: 'Standardı Karşılamıyor', definition_en: 'Does not meet standard (not demonstrated).', definition_tr: 'Standardı Karşılamıyor – Gösterilmedi' },
      { value: 'not_applicable', label_en: 'N/A', label_tr: 'Geçerli Değil (GD)', definition_en: 'N/A – Not applicable (to be used in rare cases in which the behavior as described by the statement was not relevant to the conversation or there were no opportunities for the coach to demonstrate the behavior).', definition_tr: 'Geçerli Değil (GD) – Davranış, görüşme bağlamında geçerli değildi veya koçun göstermesi için fırsat yoktu' },
    ],
    },
    competencies: [
      {
        code: 'C3',
        title_en: 'Establishes and Maintains Agreements',
        title_tr: 'Anlaşmaları Oluşturur ve Sürdürür',
        required: 3,
        total: 4,
        statements: [
          {
            id: 'M3.1',
            competency_code: 'C3',
            text_en: 'Coach partners with the client to identify the focus of the session.',
            text_tr: 'Koç, görüşmenin odağını belirlemek için müşteriyle iş birliği yapar.',
          },
          {
            id: 'M3.2',
            competency_code: 'C3',
            text_en: 'Coach explores and clarifies several aspects of the topic.',
            text_tr: 'Koç, konunun birden fazla boyutunu araştırır ve açıklığa kavuşturur.',
          },
          {
            id: 'M3.3',
            competency_code: 'C3',
            text_en: 'Coach confirms the mutual understanding of the client\'s desired session outcome.',
            text_tr: 'Koç, müşterinin görüşmeden beklediği çıktıya ilişkin karşılıklı anlayışı teyit eder.',
          },
          {
            id: 'M3.4',
            competency_code: 'C3',
            text_en: 'Coach notices possible shifts emerging in the conversation and clarifies the desired direction of the session with the client.',
            text_tr: 'Koç, görüşmede ortaya çıkan olası yön değişikliklerini fark eder ve müşteriyle birlikte görüşmenin istenen yönünü açıklığa kavuşturur.',
          },
        ],
      },
      {
        code: 'C4',
        title_en: 'Cultivates Trust and Safety',
        title_tr: 'İtimat ve Güven Oluşturur',
        required: 3,
        total: 4,
        statements: [
          {
            id: 'M4.1',
            competency_code: 'C4',
            text_en: 'Coach acknowledges the client\'s learning or growth at any moment in the session.',
            text_tr: 'Koç, görüşmenin herhangi bir anında müşterinin öğrenmesini veya gelişimini kabul eder / takdir eder.',
          },
          {
            id: 'M4.2',
            competency_code: 'C4',
            text_en: 'Coach recognizes the client\'s emotions, strengths, or other unique characteristics.',
            text_tr: 'Koç, müşterinin duygularını, güçlü yönlerini veya diğer özgün niteliklerini fark eder ve kabul eder.',
          },
          {
            id: 'M4.3',
            competency_code: 'C4',
            text_en: 'Coach seeks to understand the client by inviting the client to share more about themselves, their perspectives, or their identity.',
            text_tr: 'Koç, müşteriyi kendisi, perspektifleri veya kimliği hakkında daha fazla paylaşmaya davet ederek müşteriyi anlamaya çalışır.',
          },
          {
            id: 'M4.4',
            competency_code: 'C4',
            text_en: 'Coach demonstrates empathy for what the client has expressed.',
            text_tr: 'Koç, müşterinin ifade ettiklerine karşı empati gösterir.',
          },
        ],
      },
      {
        code: 'C5',
        title_en: 'Maintains Presence',
        title_tr: 'Koç Var Olma Halini Sürdürür',
        required: 3,
        total: 4,
        statements: [
          {
            id: 'M5.1',
            competency_code: 'C5',
            text_en: 'Coach responds holistically to the client in a manner that keeps the conversation flowing.',
            text_tr: 'Koç, konuşmanın akışını sürdürecek biçimde müşteriye bütüncül olarak yanıt verir.',
          },
          {
            id: 'M5.2',
            competency_code: 'C5',
            text_en: 'Coach partners with the client in ways that support the client needs.',
            text_tr: 'Koç, müşterinin ihtiyaçlarını destekleyecek biçimlerde müşteriyle iş birliği yapar.',
          },
          {
            id: 'M5.3',
            competency_code: 'C5',
            text_en: 'Coach consistently demonstrates curiosity.',
            text_tr: 'Koç, tutarlı biçimde merak gösterir.',
          },
          {
            id: 'M5.4',
            competency_code: 'C5',
            text_en: 'Coach creates space for silence, pause, or reflection',
            text_tr: 'Koç, sessizlik, duraklama veya yansıma için alan yaratır.',
          },
        ],
      },
      {
        code: 'C6',
        title_en: 'Listens Actively',
        title_tr: 'Etkin Dinler',
        required: 2,
        total: 3,
        statements: [
          {
            id: 'M6.1',
            competency_code: 'C6',
            text_en: 'Coach recognizes the nuances of language, emotions, energy, or behaviors in relation to the client and the client\'s agenda.',
            text_tr: 'Koç, müşteri ve müşterinin gündemiyle ilişkili olarak dil, duygu, enerji veya davranışlardaki nüansları fark eder.',
          },
          {
            id: 'M6.2',
            competency_code: 'C6',
            text_en: 'Coach\'s response demonstrates understanding of the client\'s thinking, feeling, or insight in the moment.',
            text_tr: 'Koçun yanıtı, müşterinin o andaki düşünce, duygu veya içgörüsünü kavradığını gösterir.',
          },
          {
            id: 'M6.3',
            competency_code: 'C6',
            text_en: 'Coach explores what the client communicates in relation to multiple dimensions of the client.',
            text_tr: 'Koç, müşterinin ilettiğini müşterinin birden fazla boyutuyla ilişkili olarak araştırır.',
          },
        ],
      },
      {
        code: 'C7',
        title_en: 'Evokes Awareness',
        title_tr: 'Farkındalık Yaratır',
        required: 2,
        total: 3,
        statements: [
          {
            id: 'M7.1',
            competency_code: 'C7',
            text_en: 'Coach partners with the client to explore and expand the client\'s perspective.',
            text_tr: 'Koç, müşterinin perspektifini keşfetmesi ve genişletmesi için müşteriyle iş birliği yapar.',
          },
          {
            id: 'M7.2',
            competency_code: 'C7',
            text_en: 'Coach shares insights, sensations, or observations that support the client in creating new or expanded awareness.',
            text_tr: 'Koç, müşterinin yeni veya genişletilmiş farkındalık oluşturmasını desteklemek amacıyla içgörülerini, hislerini veya gözlemlerini paylaşır.',
          },
          {
            id: 'M7.3',
            competency_code: 'C7',
            text_en: 'Coach stimulates new client insights with succinct, open-ended questions, one at a time.',
            text_tr: 'Koç, bir seferde bir tane olmak üzere, özlü ve açık uçlu sorularla müşteride yeni içgörüler uyandırır.',
          },
        ],
      },
      {
        code: 'C8',
        title_en: 'Facilitates Client Growth',
        title_tr: 'Müşterinin Gelişimini Kolaylaştırır',
        required: 2,
        total: 3,
        statements: [
          {
            id: 'M8.1',
            competency_code: 'C8',
            text_en: 'Coach invites the client to reflect on what they have learned about themselves from the coaching conversation.',
            text_tr: 'Koç, müşteriyi koçluk görüşmesinden kendileri hakkında öğrendiklerini yansıtmaya davet eder.',
          },
          {
            id: 'M8.2',
            competency_code: 'C8',
            text_en: 'Coach partners with the client to translate insights or learning into actions.',
            text_tr: 'Koç, içgörüleri veya öğrenilenleri eylemlere dönüştürmek için müşteriyle iş birliği yapar.',
          },
          {
            id: 'M8.3',
            competency_code: 'C8',
            text_en: 'Coach partners with the client to complete the session.',
            text_tr: 'Koç, görüşmeyi kapatmak için müşteriyle iş birliği yapar.',
          },
        ],
      },
    ],
  },
};

export const ICF_FORM_LEVELS: IcfFormLevel[] = ['acc', 'pcc', 'mcc'];

export function getLevelForm(level: IcfFormLevel): IcfLevelForm {
  return ICF_FORMS[level];
}

/** Flat, form-ordered list of every statement for a level (20 / 37 / 21). */
export function getStatements(level: IcfFormLevel): IcfFormStatement[] {
  return ICF_FORMS[level].competencies.flatMap((competency) => competency.statements);
}

export function getStatement(level: IcfFormLevel, statementId: string): IcfFormStatement | undefined {
  return getStatements(level).find((statement) => statement.id === statementId);
}

export function getStatementText(
  level: IcfFormLevel,
  statementId: string,
  language: string,
): string {
  const statement = getStatement(level, statementId);
  if (!statement) return statementId;
  return language === 'tr' ? statement.text_tr : statement.text_en;
}

export function getScaleOptions(level: IcfFormLevel): IcfScaleOption[] {
  return ICF_FORMS[level].scale.options;
}

export function getScaleOptionLabel(
  level: IcfFormLevel,
  value: StatementRatingValue,
  language: string,
): string {
  const option = getScaleOptions(level).find((item) => item.value === value);
  if (!option) return String(value);
  return language === 'tr' ? option.label_tr : option.label_en;
}

/** CRF gate thresholds per competency, keyed by competency code. */
export function getThresholds(level: IcfFormLevel): Record<IcfCompetencyCode, { required: number; total: number }> {
  const result = {} as Record<IcfCompetencyCode, { required: number; total: number }>;
  ICF_FORMS[level].competencies.forEach((competency) => {
    result[competency.code] = { required: competency.required, total: competency.total };
  });
  return result;
}

export function getCompetencyTitle(
  level: IcfFormLevel,
  code: IcfCompetencyCode,
  language: string,
): string {
  const competency = ICF_FORMS[level].competencies.find((item) => item.code === code);
  if (!competency) return code;
  return language === 'tr' ? competency.title_tr : competency.title_en;
}

/**
 * §1.3 union rule: a rating counts as Proficiency Demonstrated when it is
 * Meets/Exceeds (ACC/MCC) or Observed (PCC). N/A never counts.
 */
export function isDemonstrated(level: IcfFormLevel, rating: StatementRatingValue | null | undefined): boolean {
  if (!rating) return false;
  if (level === 'pcc') return rating === 'observed';
  return rating === 'meets' || rating === 'exceeds';
}

